/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTheme } from '../../context/ThemeContext';
import axiosClient from '../../api/axiosClient';
import toast from 'react-hot-toast';
import { Sparkles, Palette, Eye, Loader2, Image as ImageIcon, Wand2, Camera } from 'lucide-react';
import MakeupPresetCard from '../../components/makeup/MakeupPresetCard';
import IntensitySliders from '../../components/makeup/IntensitySliders';
import PromptBox from '../../components/makeup/PromptBox';
import ResultStrip from '../../components/makeup/ResultStrip';
import ClientSearchModal from '../../components/ClientSearchModal';
import MakeupLookPanel from '../../components/makeup/MakeupLookPanel';
import CameraCapture from '../../components/haircutsStudio/CameraCapture';
import BeforeAfterViewer from '../../components/haircutsStudio/BeforeAfterViewer';
import { makeupStudioLooks } from '../../data/makeupStudioLooks';
import { buildMakeupLookPrompt } from '../../utils/makeupLookPromptBuilder';
import { generateMakeupLook } from '../../services/makeupLookApi';
import {
  clearMakeupStudioSession,
  loadMakeupStudioSession,
  saveMakeupStudioSession,
} from '../../utils/haircutsStudioSession';

// Brand accent color
const ACCENT = '#7d6633';
const MOBILE_VIEWPORT_QUERY = '(max-width: 767px)';

const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const useIsMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_VIEWPORT_QUERY).matches : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener?.('change', updateViewport);
    return () => mediaQuery.removeEventListener?.('change', updateViewport);
  }, []);

  return isMobile;
};

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const CATEGORY_TABS = [
  { id: 'all', label: 'All', icon: Sparkles },
  { id: 'lip', label: 'Lips', icon: '💄' },
  // NOTE: The existing `eye` region is eyeshadow (separate from `liner`)
  { id: 'eye', label: 'Eyeshadow', icon: Eye },
  { id: 'concealer', label: 'Concealer', icon: '🩹' },
  { id: 'blush', label: 'Blush', icon: '🌸' },
  { id: 'highlighter', label: 'Highlighter', icon: '✨' },
  { id: 'brow', label: 'Brows', icon: '📏' },
  { id: 'liner', label: 'Liner', icon: '✏️' },
  { id: 'combo', label: 'Combos', icon: Palette }
];

const MakeupStudio = () => {
  const { user } = useAdminAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const isMobileViewport = useIsMobileViewport();
  
  // State
  const [presets, setPresets] = useState([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  
  // Scan and image state
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [sourceImageUrl, setSourceImageUrl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showClientRequiredModal, setShowClientRequiredModal] = useState(false);
  const fileInputRef = useRef(null);
  const resultCardRef = useRef(null);
  const sessionSaveWarningShownRef = useRef(false);
  const generationPhaseTimersRef = useRef([]);
  
  // PERSISTENT ADDITIVE MAKEUP STATE
  // Each section maintains its own makeup configuration
  const createDefaultMakeupState = useCallback(() => ({
    lip: { presetIds: [], intensity: 0, applied: false },
    eye: { presetIds: [], intensity: 0, applied: false },
    concealer: { presetIds: [], intensity: 0, applied: false },
    blush: { presetIds: [], intensity: 0, applied: false },
    highlighter: { presetIds: [], intensity: 0, applied: false },
    brow: { presetIds: [], intensity: 0, applied: false },
    liner: { presetIds: [], intensity: 0, applied: false }
  }), []);

  const normalizeMakeupState = useCallback((incoming) => {
    const base = createDefaultMakeupState();
    if (!incoming || typeof incoming !== 'object') return base;
    Object.keys(base).forEach((key) => {
      const v = incoming[key];
      if (v && typeof v === 'object') {
        base[key] = {
          ...base[key],
          ...v,
          presetIds: Array.isArray(v.presetIds) ? v.presetIds : base[key].presetIds
        };
      }
    });
    return base;
  }, [createDefaultMakeupState]);

  const [makeupState, setMakeupState] = useState(() => createDefaultMakeupState());

  // Helper: determine if selected presets include a category (for lips which have NO intensity control)
  const hasSelectedCategory = useCallback((categoryId, presetIdsList) => {
    if (!Array.isArray(presetIdsList) || presetIdsList.length === 0) return false;
    const presetById = new Map((Array.isArray(presets) ? presets : []).map(p => [p.id, p]));
    return presetIdsList.some(id => presetById.get(id)?.category === categoryId);
  }, [presets]);
  
  // Custom prompt
  const [customPrompt, setCustomPrompt] = useState('');

  // Debug: show region mask overlay (Preview only)
  const [debugMaskOverlay, setDebugMaskOverlay] = useState(false);
  
  // Results
  const [results, setResults] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  // Right-panel: standalone "look" presets (separate from left panel)
  const [selectedLookId, setSelectedLookId] = useState(null);
  const [lookGenerating, setLookGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState(0);
  const [sessionHydrated, setSessionHydrated] = useState(false);
  
  // Fetch presets on mount
  useEffect(() => {
    fetchPresets();
    fetchScans();
  }, []);

  useEffect(() => {
    const savedSession = loadMakeupStudioSession();
    if (!savedSession) {
      setSessionHydrated(true);
      return undefined;
    }

    setSelectedClient(savedSession.selectedClient || null);
    setSelectedScan(savedSession.selectedScan || null);
    setSourceImageUrl(savedSession.sourceImageUrl || null);
    setSelectedLookId(savedSession.selectedLookId || null);
    setCustomPrompt(savedSession.customPrompt || '');
    setResults(Array.isArray(savedSession.results) ? savedSession.results : []);
    setSelectedResult(savedSession.selectedResult || null);
    setPreviewUrl(savedSession.previewUrl || null);
    setSessionHydrated(true);

    return () => {
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!sessionHydrated) return;

    const hasSessionData =
      !!selectedClient ||
      !!selectedScan ||
      !!sourceImageUrl ||
      !!selectedLookId ||
      !!customPrompt.trim() ||
      results.length > 0 ||
      !!selectedResult ||
      !!previewUrl;

    if (!hasSessionData) {
      clearMakeupStudioSession();
      return;
    }

    const didSave = saveMakeupStudioSession({
      selectedClient,
      selectedScan,
      sourceImageUrl,
      selectedLookId,
      customPrompt,
      results,
      selectedResult,
      previewUrl,
    });

    if (!didSave && !sessionSaveWarningShownRef.current) {
      sessionSaveWarningShownRef.current = true;
      toast.error(t('admin.haircuts_session_save_failed'));
    }
  }, [
    customPrompt,
    previewUrl,
    results,
    selectedClient,
    selectedLookId,
    selectedResult,
    selectedScan,
    sessionHydrated,
    sourceImageUrl,
    t,
  ]);

  useEffect(() => () => {
    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];
  }, []);
  
  // Fetch presets from API
  const fetchPresets = async (category = null) => {
    try {
      const params = category && category !== 'all' ? { category } : {};
      const response = await axiosClient.get('/makeup/presets', { params });
      const data = response?.data;
      // Defensive: ensure presets is always an array
      const list = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : []);
      setPresets(list);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch makeup presets:', error);
      toast.error('Failed to load makeup presets');
      setPresets([]);
      setLoading(false);
    }
  };
  
  // Fetch recent scans
  const fetchScans = async () => {
    try {
      const response = await axiosClient.get('/scans?limit=20');
      const data = response?.data;
      // Defensive: accept various shapes {rows:[]}, {data:[]}, []
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.rows)
            ? data.rows
            : (Array.isArray(data?.data)
                ? data.data
                : (Array.isArray(data?.scans) ? data.scans : [])));
      setScans(list);
    } catch (error) {
      console.error('Failed to fetch scans:', error);
      setScans([]);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    fetchPresets(categoryId);
  };
  
  // Toggle preset selection for active category
  const togglePreset = (presetId) => {
    if (activeCategory === 'all' || activeCategory === 'combo') {
      // In "all" or "combo" mode, update all relevant sections
      setSelectedPresetIds(prev => {
        if (prev.includes(presetId)) {
          return prev.filter(id => id !== presetId);
        } else {
          return [...prev, presetId];
        }
      });
    } else {
      // In single-section mode, update only that section's presets
      setMakeupState(prev => {
        const currentPresets = prev[activeCategory].presetIds;
        const newPresets = currentPresets.includes(presetId)
          ? currentPresets.filter(id => id !== presetId)
          : [...currentPresets, presetId];
        
        return {
          ...prev,
          [activeCategory]: {
            ...prev[activeCategory],
            presetIds: newPresets
          }
        };
      });
    }
  };
  
  // Update intensity for a specific section
  const updateSectionIntensity = (region, intensity) => {
    setMakeupState(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        intensity: parseFloat(intensity)
      }
    }));
  };
  
  // Handle scan selection
  const handleScanSelect = (scan) => {
    setSelectedScan(scan);
    const imageUrl = scan.original_image_path || scan.image_url;
    setSourceImageUrl(imageUrl);
    
    // Load previous makeup settings if they exist
    if (scan.makeup_json) {
      const makeup = scan.makeup_json;
      
      // Load layered makeup state if available
      if (makeup.makeupState) {
        setMakeupState(normalizeMakeupState(makeup.makeupState));
      } else if (makeup.intensities) {
        // Fallback: convert old format to new format
        const newState = createDefaultMakeupState();
        Object.keys(makeup.intensities).forEach(region => {
          newState[region] = {
            presetIds: makeup.selectedPresetIds || [],
            intensity: makeup.intensities[region] || 0,
            applied: makeup.intensities[region] > 0
          };
        });
        setMakeupState(normalizeMakeupState(newState));
      }
      
      if (makeup.customPrompt) {
        setCustomPrompt(makeup.customPrompt);
      }
    }
    
    // Load previous results if they exist
    if (scan.makeup_result_urls && scan.makeup_result_urls.length > 0) {
      setResults(scan.makeup_result_urls);
    }
  };

  // Handle client selection from search
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setShowClientSearch(false);
    toast.success(`Selected client: ${client.name}`);
  };

  // Upload a new client image and create a Scan (inline, like Capture AI)
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!selectedClient) {
      setShowClientRequiredModal(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Please select an image under 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // Show loading toast
    const uploadToast = toast.loading('Uploading client image...');
    
    try {
      const form = new FormData();
      form.append('client_name', selectedClient.name || 'Client');
      form.append('salon_id', selectedClient.shop_id || 3); // fallback
      form.append('barber_id', user?.id || '');
      form.append('image', file); // ✅ Changed from 'file' to 'image' to match backend
      
      const resp = await axiosClient.post('/scans/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Client image uploaded successfully!', { id: uploadToast });
      
      // Reload scans list
      await fetchScans();
      
      // Auto-select the newly created scan
      if (resp.data?.scan) {
        handleScanSelect(resp.data.scan);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      toast.error(`Upload failed: ${msg}`, { id: uploadToast });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Generate preview
  const handlePreview = async () => {
    if (!selectedScan) {
      toast.error('Please select a client image first');
      return;
    }
    
    // Validation: Check if current section has makeup configured
    if (activeCategory !== 'all' && activeCategory !== 'combo') {
      const currentSection = makeupState[activeCategory];
      if (currentSection.intensity === 0 && currentSection.presetIds.length === 0) {
        toast.error(`Please set intensity or select presets for ${activeCategory} section`);
        return;
      }
    }
    
    setPreviewing(true);
    
    try {
      // CRITICAL: Use session state for layered workflow
      // Must be a boolean (NOT a string), otherwise backend receives URL and disables session usage.
      const hasExistingMakeup = Boolean(previewUrl) || results.length > 0;
      
      // Send ONLY active region's data (backend expects JSON)
      const currentPresets = activeCategory === 'all' || activeCategory === 'combo'
        ? selectedPresetIds
        : makeupState[activeCategory]?.presetIds || [];
      
      const currentIntensities = activeCategory === 'all' || activeCategory === 'combo'
        ? (() => {
            const base = Object.fromEntries(Object.keys(makeupState).map(k => [k, makeupState[k].intensity]));
            // Lips have NO intensity slider; if any lip preset is selected in All/Combo mode, force full application.
            const hasLipPreset = hasSelectedCategory('lip', currentPresets);
            return { ...base, lip: hasLipPreset ? 1 : 0 };
          })()
        : { [activeCategory]: makeupState[activeCategory]?.intensity || 0 };
      
      const response = await axiosClient.post('/makeup/preview', {
        scanId: selectedScan.id,
        presetIds: currentPresets,
        customPrompt: customPrompt || '',
        intensities: currentIntensities,
        activeRegion: activeCategory,
        useSessionState: hasExistingMakeup,
        debugMask: debugMaskOverlay
      });
      
      setPreviewUrl(response.data.resultUrl);
      
      // Mark current section as applied
      if (activeCategory !== 'all' && activeCategory !== 'combo') {
        setMakeupState(prev => ({
          ...prev,
          [activeCategory]: {
            ...prev[activeCategory],
            applied: true
          }
        }));
      }
      
      const sectionLabel = activeCategory === 'all' ? 'Combined' : CATEGORY_TABS.find(t => t.id === activeCategory)?.label;
      if (debugMaskOverlay) {
        toast.success(`Mask overlay preview: ${sectionLabel}`);
      } else {
        toast.success(`${sectionLabel} makeup applied! ${hasExistingMakeup ? '(Layered on previous)' : ''}`);
      }
    } catch (error) {
      console.error('Preview failed:', error);
      const message = error.response?.data?.error?.message || 'Preview generation failed';
      toast.error(message);
    } finally {
      setPreviewing(false);
    }
  };
  
  // Generate final results
  const handleGenerate = async (variants = 2) => {
    if (!selectedScan) {
      toast.error('Please select a client image first');
      return;
    }
    
    // Build combined intensities and presets from ALL applied sections
    const combinedIntensities = {};
    const combinedPresetIds = [];
    let hasAnyMakeup = false;
    
    Object.keys(makeupState).forEach(region => {
      const section = makeupState[region];
      if (section.intensity > 0 || section.presetIds.length > 0) {
        hasAnyMakeup = true;
        combinedIntensities[region] = section.intensity;
        combinedPresetIds.push(...section.presetIds);
      } else {
        combinedIntensities[region] = 0;
      }
    });

    // Lips have NO intensity slider; if any lip preset is included anywhere, force full application.
    // This preserves existing flow while ensuring lips are applied as color-only.
    if ((makeupState.lip?.presetIds || []).length > 0) {
      combinedIntensities.lip = 1;
    }
    
    // Validation: Check if we have something to apply
    if (!hasAnyMakeup && !customPrompt.trim()) {
      toast.error('Please apply makeup to at least one region or enter a custom prompt');
      return;
    }
    
    // Validate single-section mode
    if (activeCategory !== 'all' && activeCategory !== 'combo') {
      const currentSection = makeupState[activeCategory];
      if (currentSection.intensity === 0 && currentSection.presetIds.length === 0) {
        toast.error(`Please set intensity or select presets for ${activeCategory} section`);
        return;
      }
    }
    
    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];
    setGenerationPhase(1);
    setGenerating(true);
    
    try {
      if (isMobileViewport) {
        await waitForNextPaint();
        resultCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(2), 1500));
      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(3), 4000));

      const response = await axiosClient.post('/makeup/generate', {
        scanId: selectedScan.id,
        presetIds: [...new Set(combinedPresetIds)], // Remove duplicates
        customPrompt,
        intensities: combinedIntensities,
        variants,
        activeRegion: 'all', // Always send 'all' to apply combined makeup
        useClientMasks: false
      });

      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      setGenerationPhase(4);

      if (isMobileViewport) {
        await new Promise(resolve => window.setTimeout(resolve, 700));
      }
      
      setResults(response.data.resultUrls);
      toast.success(`Generated ${response.data.resultUrls.length} makeup variant(s) with all layers!`);
      window.dispatchEvent(new Event('credits:changed'));
      
      // Mark all sections with intensity > 0 as applied
      const updatedState = { ...makeupState };
      Object.keys(updatedState).forEach(region => {
        if (updatedState[region].intensity > 0 || updatedState[region].presetIds.length > 0) {
          updatedState[region].applied = true;
        }
      });
      setMakeupState(updatedState);
      
      // Update selected scan with new data
      setSelectedScan(prev => ({
        ...prev,
        makeup_json: {
          ...response.data.scan.makeup_json,
          makeupState: updatedState
        },
        makeup_result_urls: response.data.scan.makeup_result_urls
      }));
      
    } catch (error) {
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      console.error('Generation failed:', error);
      const message = error.response?.data?.error?.message || 'Makeup generation failed';
      toast.error(message);
    } finally {
      setGenerationPhase(0);
      setGenerating(false);
    }
  };

  // Right-panel: generate a full makeup look from a single prompt (does NOT touch left panel state)
  const handleGenerateLook = async (variants = 1) => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      setShowClientSearch(true);
      return;
    }

    if (!selectedScan) {
      toast.error('Please upload/select a client image first');
      return;
    }

    const look = makeupStudioLooks.find((l) => l.id === selectedLookId);
    if (!look) {
      toast.error('Please select a makeup look first');
      return;
    }

    // Prevent overlap with existing generation/preview flows
    if (generating || previewing || lookGenerating) return;

    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];
    setGenerationPhase(1);
    setLookGenerating(true);

    try {
      if (isMobileViewport) {
        await waitForNextPaint();
        resultCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(2), 1500));
      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(3), 4000));

      const prompt = buildMakeupLookPrompt({ look });
      const response = await generateMakeupLook({
        scanId: selectedScan.id,
        prompt,
        variants
      });

      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      setGenerationPhase(4);

      if (isMobileViewport) {
        await new Promise(resolve => window.setTimeout(resolve, 700));
      }

      setPreviewUrl(null);
      setSelectedResult(null);
      setResults(response.resultUrls);
      toast.success(`Generated ${response.resultUrls.length} look variant(s): ${look.name}`);
      window.dispatchEvent(new Event('credits:changed'));

      // Keep scan state in sync (but do NOT modify makeupState / selectedPresetIds / customPrompt)
      if (response?.scan) {
        setSelectedScan((prev) => ({
          ...prev,
          makeup_json: response.scan.makeup_json,
          makeup_result_urls: response.scan.makeup_result_urls
        }));
      }
    } catch (error) {
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      console.error('Look generation failed:', error);
      const message = error.response?.data?.error?.message || 'Makeup look generation failed';
      toast.error(message);
    } finally {
      setGenerationPhase(0);
      setLookGenerating(false);
    }
  };
  
  // Section-scoped clear: reset only active section or all if "All" selected
  const handleClear = async () => {
    if (activeCategory === 'all' || activeCategory === 'combo') {
      // Global reset - clear everything
      setSelectedPresetIds([]);
      setCustomPrompt('');
      setMakeupState(createDefaultMakeupState());
      setPreviewUrl(null);
      setResults([]);
      
      // Reset session state on backend
      if (selectedScan) {
        try {
          await axiosClient.post('/makeup/reset-session', {
            scanId: selectedScan.id
          });
        } catch (error) {
          console.error('Failed to reset session:', error);
        }
      }
      
      toast.success('All makeup cleared');
    } else {
      // Section-specific reset - clear ONLY active section
      setMakeupState(prev => ({
        ...prev,
        [activeCategory]: {
          presetIds: [],
          intensity: 0,
          applied: false
        }
      }));
      
      // Clear preview/results only if no other sections have makeup
      const otherSectionsHaveMakeup = Object.keys(makeupState).some(
        region => region !== activeCategory && 
                 (makeupState[region].intensity > 0 || makeupState[region].presetIds.length > 0)
      );
      
      if (!otherSectionsHaveMakeup) {
        setPreviewUrl(null);
        setResults([]);
        
        // Reset session state on backend
        if (selectedScan) {
          try {
            await axiosClient.post('/makeup/reset-session', {
              scanId: selectedScan.id
            });
          } catch (error) {
            console.error('Failed to reset session:', error);
          }
        }
      }
      
      const sectionLabel = CATEGORY_TABS.find(t => t.id === activeCategory)?.label || activeCategory;
      toast.success(`${sectionLabel} makeup cleared`);
    }
  };

  const handleDownload = async (imageUrl) => {
    try {
      toast.loading('Downloading image...');
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `makeup-${selectedClient?.name?.replace(/\s+/g, '-') || 'result'}-${Date.now()}.jpg`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      window.setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);
      toast.dismiss();
      toast.success('Image downloaded successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Download failed. Please try again or right-click the image to save.');
    }
  };
  
  // Get filtered presets and selected presets based on mode
  const presetList = Array.isArray(presets) ? presets : [];
  const filteredPresets = presetList;
  
  // Determine which presets are selected based on active mode
  const getSelectedPresetIds = () => {
    if (activeCategory === 'all' || activeCategory === 'combo') {
      return selectedPresetIds;
    } else {
      return makeupState[activeCategory]?.presetIds || [];
    }
  };
  
  const currentSelectedPresetIds = getSelectedPresetIds();
  const selectedPresets = presetList.filter(p => currentSelectedPresetIds.includes(p.id));
  const primaryResultImage = selectedResult || previewUrl || (results.length > 0 ? results[0] : null);
  const isMakeupResultLoading = generating || previewing || lookGenerating;
  
  return (
    <div className="relative min-h-screen pb-32 md:pb-6">
      {/* Brand Glass Morphism Background Elements - Hidden in light mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden dark:block">
        {/* Primary brand gradient orbs */}
        <div className="absolute top-10 left-10 w-96 h-96 bg-gradient-to-br from-[#7d6633]/25 to-[#5a4d26]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-32 right-16 w-80 h-80 bg-gradient-to-br from-[#a0824a]/20 to-[#7d6633]/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-gradient-to-br from-[#d4b47a]/15 to-[#a0824a]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 right-20 w-64 h-64 bg-gradient-to-br from-[#7d6633]/30 to-[#d4b47a]/15 rounded-full blur-3xl"></div>
        
        {/* Secondary brand accents */}
        <div className="absolute top-1/3 left-1/4 w-56 h-56 bg-gradient-to-br from-[#8b7340]/18 to-[#6d5b2e]/22 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/3 right-1/3 w-48 h-48 bg-gradient-to-br from-[#c4a76a]/12 to-[#9a7f4f]/18 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/2 w-40 h-40 bg-gradient-to-br from-[#7d6633]/20 to-[#b8955d]/15 rounded-full blur-xl"></div>
      </div>

      <div className="relative space-y-6 z-10">
        {/* Header (match Hairstyle pages sizing) */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
                <Palette className="h-6 w-6 mr-2" style={{ color: ACCENT }} />
                {t('admin.makeup_studio_title')}
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                {t('admin.makeup_studio_subtitle')}
              </p>
            </div>
          </div>
        </div>
      
      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Sidebar - Makeup Looks */}
        <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
          <Card>
            <CardHeader
              title={t('admin.section_makeup_looks')}
              subtitle={selectedLookId ? t('admin.makeup_look_selected') : t('admin.makeup_choose_look')}
              icon={<Sparkles className="h-5 w-5" style={{ color: ACCENT }} />}
              action={
                selectedLookId && (
                  <button
                    type="button"
                    onClick={() => setSelectedLookId(null)}
                    className="text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                    disabled={generating || previewing || lookGenerating}
                  >
                    Clear
                  </button>
                )
              }
            />
            <div className="p-4">
              <MakeupLookPanel
                looks={makeupStudioLooks}
                selectedLookId={selectedLookId}
                onSelect={(look) => setSelectedLookId(look.id)}
                disabled={generating || previewing || lookGenerating}
              />
              {!selectedScan && (
                <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400 text-center">
                  {t('admin.makeup_select_image')}
                </p>
              )}
            </div>
          </Card>
        </div>
        
        {/* Center - Results (Large Display) */}
        <div className="lg:col-span-6 space-y-4 order-3 lg:order-2 flex flex-col">
          {/* Results Display */}
          <div ref={resultCardRef} className="order-1 lg:order-1">
            <Card>
            <CardHeader
              title={t('admin.section_makeup_results')}
              subtitle={results.length > 0 ? `${results.length} ${t('admin.makeup_results_count')}` : previewUrl ? t('admin.makeup_preview_ready') : t('admin.results_will_appear')}
              icon={<Sparkles className="h-5 w-5" style={{ color: ACCENT }} />}
            />
            <div className="p-4">
              <div className="md:hidden">
                <BeforeAfterViewer
                  beforeImage={sourceImageUrl}
                  afterImage={primaryResultImage}
                  loading={isMakeupResultLoading}
                  onDownload={handleDownload}
                  generationPhase={generationPhase}
                  loadingStepLabels={[
                    t('admin.mobile_generation_upload_received'),
                    t('admin.mobile_generation_analyzing_image'),
                    t('admin.mobile_generation_applying_selected_style'),
                    t('admin.mobile_generation_rendering_result'),
                  ]}
                />
              </div>

              <div className="hidden md:block relative w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl overflow-hidden" style={{ minHeight: '500px' }}>
                {primaryResultImage ? (
                  <>
                    <img
                      src={primaryResultImage}
                      alt="Makeup Result"
                      className="w-full h-full object-contain"
                    />
                    {(generating || previewing || lookGenerating) && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                        <div className="text-center text-white">
                          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-2" />
                          <p className="text-sm">{lookGenerating ? t('admin.generating_look') : previewing ? t('admin.generating_preview') : t('admin.generating_makeup')}</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                    <div className="text-center">
                      <Sparkles className="h-16 w-16 mx-auto mb-2 opacity-50" />
                      <p className="text-lg font-medium mb-2">{t('admin.no_results_yet')}</p>
                      <p className="text-sm">{t('admin.no_results_subtitle')}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Result Thumbnails */}
              {results.length > 0 && (
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {results.map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedResult(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedResult === url
                          ? 'border-pink-500 ring-2 ring-pink-500/50'
                          : 'border-transparent hover:border-pink-300'
                      }`}
                    >
                      <img src={url} alt={`Result ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            </Card>
          </div>
          
          {/* Custom Prompt */}
          <Card className="order-2 lg:order-2">
            <CardHeader
              title={t('admin.section_custom_prompt')}
              subtitle={t('admin.custom_prompt_optional')}
              icon={<Wand2 className="h-5 w-5" style={{ color: ACCENT }} />}
            />
            <div className="p-4 space-y-3">
              <PromptBox
                value={customPrompt}
                onChange={setCustomPrompt}
                disabled={generating || previewing}
              />
              
              {/* Generate Selected Look Button - Moved from Right Panel */}
              <button
                type="button"
                onClick={() => handleGenerateLook(1)}
                disabled={generating || previewing || lookGenerating}
                className="hidden md:flex w-full items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold transition-all shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {lookGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('admin.generating_look')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    {t('admin.generate_selected_look')}
                  </>
                )}
              </button>
            </div>
          </Card>
          
        </div>
        
        {/* Right Sidebar - Client Image & Upload */}
        <div className="lg:col-span-3 space-y-4 order-1 lg:order-3">
          {/* Client Selection & Upload */}
          <Card>
            <CardHeader
              title={t('admin.section_client_image')}
              subtitle={t('admin.client_upload_subtitle')}
              icon={<Camera className="h-5 w-5" style={{ color: ACCENT }} />}
            />
            <div className="p-4 space-y-4">
              {/* Client Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('admin.client_label')}</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowClientSearch(true)}
                    className="px-3 py-1.5 rounded-lg text-xs bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium"
                  >
                    {selectedClient ? t('admin.client_change') : t('admin.client_select')}
                  </motion.button>
                </div>
                {selectedClient ? (
                  <div className="p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="text-pink-600 dark:text-pink-400">👤</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{selectedClient.name}</p>
                        {selectedClient.email && (
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{selectedClient.email}</p>
                        )}
                      </div>
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">✅</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('admin.no_client_selected')}</p>
                )}
              </div>

              {/* Upload Section */}
              <div className="border-2 border-dashed p-4 rounded-xl bg-white dark:bg-zinc-800/30 text-center border-pink-200 dark:border-pink-900/30">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="mkp-file-upload"
                />
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                  <label
                    htmlFor="mkp-file-upload"
                    onClick={(e) => {
                      if (!selectedClient) {
                        e.preventDefault();
                        toast.error('Please select a client first');
                        setShowClientSearch(true);
                      }
                    }}
                    className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                      !selectedClient
                        ? 'cursor-not-allowed opacity-60 bg-zinc-300 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200'
                        : 'cursor-pointer bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white'
                    }`}
                  >
                    <ImageIcon className="h-4 w-4" />
                    {t('admin.upload_image')}
                  </label>

                  <CameraCapture
                    onFileSelect={handleFileSelect}
                    disabled={!selectedClient || generating || previewing || lookGenerating}
                    className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                      !selectedClient || generating || previewing || lookGenerating
                        ? 'cursor-not-allowed opacity-60 bg-zinc-300 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200'
                        : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                    }`}
                    buttonLabel={t('admin.camera_take_photo')}
                    facingMode="user"
                  />
                </div>
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{t('admin.image_format_hint')}</p>
                {!selectedClient && (
                  <p className="mt-1 text-xs text-pink-600 dark:text-pink-400">{t('admin.select_client_first_warning')}</p>
                )}
              </div>
              
              {/* Source Image Preview */}
              {sourceImageUrl && (
                <div className="relative aspect-square rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/10">
                  <img
                    src={sourceImageUrl}
                    alt="Source"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm">
                    <span className="text-xs text-white font-medium">{t('admin.label_source')}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
        
      </div>

      <div
        className="md:hidden fixed inset-x-0 z-30 px-4 pointer-events-none"
        style={{ bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))' }}
      >
        <motion.button
          type="button"
          onClick={() => handleGenerateLook(1)}
          disabled={generating || previewing || lookGenerating}
          className="pointer-events-auto w-full rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-4 py-3.5 text-sm font-semibold text-white shadow-xl transition-all hover:from-pink-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-2"
          whileTap={{ scale: 0.98 }}
        >
          {lookGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('admin.generating_look')}</span>
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              <span>{t('admin.generate_selected_look')}</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Client Search Modal */}
      {typeof document !== 'undefined' &&
        createPortal(
          <ClientSearchModal
            isOpen={showClientSearch}
            onClose={() => setShowClientSearch(false)}
            onClientSelect={handleClientSelect}
            isDark={isDark}
            zIndexClassName="z-[100]"
          />,
          document.body
        )}

      {/* Client Required Modal */}
      {showClientRequiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="max-w-md w-full"
          >
            <Card>
              <div className="p-6 md:p-8">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                    <svg className="h-8 w-8 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center mb-2 text-zinc-900 dark:text-zinc-100">
                  {t('admin.client_selection_required')}
                </h3>

                {/* Message */}
                <p className="text-center text-zinc-600 dark:text-zinc-400 mb-6">
                  {t('admin.makeup_client_required_msg')}
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowClientRequiredModal(false);
                      setShowClientSearch(true);
                    }}
                    className="flex-1 px-4 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>{t('admin.select_client')}</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowClientRequiredModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition-all"
                  >
                    {t('admin.cancel')}
                  </motion.button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MakeupStudio;

